'use client';

import { m, LazyMotion, domAnimation, useInView } from 'framer-motion';
import { useRef } from 'react';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Container from '@mui/material/Container';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { Iconify } from 'src/components/iconify';
import { useLocale } from 'src/locales';
import { Map } from 'src/components/map';
import { _offices } from 'src/data/offices';

// ----------------------------------------------------------------------

export function ContactInfo({ sx, ...other }) {
  const { t } = useLocale();
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });
  const theme = localStorage.getItem('theme-mode') || 'dark';

  const _socials = [
    {
      label: 'Facebook',
      value: 'facebook',
      color: '#1877F2',
      link: 'https://www.facebook.com/ShahenApp.sa',
    },
    {
      label: 'Instagram',
      value: 'instagram',
      color: '#E4405F',
      link: 'https://www.instagram.com/shahenapp/#',
    },
    {
      label: 'LinkedIn',
      value: 'linkedin',
      color: '#0A66C2',
      link: 'https://www.linkedin.com/company/shahen-%D8%B4%D8%AD%D9%86/',
    },
    {
      label: 'X (Twitter)',
      value: 'twitter',
      color: theme === 'dark' ? '#fff' : '#000',
      link: 'https://x.com/shahenapp',
    },
  ];

  const typographyProps = { variant: 'subtitle1', sx: { mb: 1 } };

  const rowVariant = (delay = 0) => ({
    initial: { opacity: 0, y: 20 },
    animate: isInView ? { opacity: 1, y: 0 } : {},
    transition: { duration: 0.6, delay },
  });

  const renderSocials = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, mt: 0.5 }}>
      {_socials.map((social, i) => (
        <m.div key={social.label} {...rowVariant(0.2 + i * 0.1)}>
          <IconButton
            onClick={() => window.open(social.link, '_blank')}
            aria-label={social.label}
            sx={{
              color: social.color,
              '&:hover': {
                bgcolor: 'transparent',
                transform: 'scale(1.1)',
                transition: 'all 0.2s ease',
              },
            }}
          >
            {social.value === 'twitter' && (
              <Iconify icon="ri:twitter-x-fill" width={20} height={20} />
            )}
            {social.value === 'facebook' && (
              <Iconify icon="ri:facebook-fill" width={24} height={24} />
            )}
            {social.value === 'instagram' && (
              <Iconify icon="ri:instagram-line" width={24} height={24} />
            )}
            {social.value === 'linkedin' && (
              <Iconify icon="ri:linkedin-fill" width={24} height={24} />
            )}
          </IconButton>
        </m.div>
      ))}
    </Box>
  );

  return (
    <LazyMotion features={domAnimation}>
      <Box
        ref={ref}
        component="section"
        sx={[
          { pt: { xs: 3, md: 10 }, pb: { xs: 3, md: 5 }, bgcolor: 'background.whitePaper' },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
        {...other}
      >
        <Container>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'stretch',
              gap: { xs: 5, md: 8 },
            }}
          >
            {/* LEFT SIDE */}
            <m.div {...rowVariant(0.2)} style={{ flex: '1 1 100%', md: '0 0 35%' }}>
              <m.div {...rowVariant(0)}>
                <Typography
                  variant="h1"
                  sx={{
                    //  fontSize: { xs: 30, md: 45 }
                    fontSize: '2rem !important',
                  }}
                >
                  {t('Get_In_touch')}
                </Typography>
              </m.div>

              <m.div {...rowVariant(0.1)}>
                <Typography variant="subtitle1" sx={{ mt: 2, mb: { xs: 4, md: 8 } }}>
                  {t('We_would_love_to_talk_about_how_we_can_help_you')}
                </Typography>
              </m.div>
              <Box
                sx={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 3 }}
              >
                <Box>
                  <Typography {...typographyProps}>{t('Email')}</Typography>
                  <Link variant="body2" color="inherit" href="mailto:info@shahen-sa.com">
                    info@shahen-sa.com
                  </Link>
                </Box>

                <Box>
                  <Typography {...typographyProps}>{t('Phone')}</Typography>
                  <Typography variant="body2">920012163</Typography>
                </Box>

                <Box>
                  <Typography {...typographyProps}>{t('Address')}</Typography>
                  <Typography variant="body2">
                    Riyadh – Al Sulaymaniyah District – Eng. Musaad Al Anqari St
                  </Typography>
                </Box>

                <Box>
                  <Typography {...typographyProps}>{t('Follow_Us')}</Typography>
                  {renderSocials()}
                </Box>
              </Box>
            </m.div>

            {/* RIGHT SIDE */}
            <m.div {...rowVariant(0.3)} style={{ flex: '1 1 100%', md: '0 0 60%' }}>
              <Box sx={{ minHeight: 320, borderRadius: 2, overflow: 'hidden' }}>
                <Map locations={_offices} />
              </Box>
            </m.div>
          </Box>
        </Container>
      </Box>
    </LazyMotion>
  );
}
